import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import type { ReactNode } from 'react';
import { useLayoutEffect, useMemo } from 'react';

import { sessionStorage } from '#src/lib/storage/sessionStorage.ts';

import type { FeatureFlagsContextType } from './index.ts';
import { FeatureFlagsContext } from './index.ts';

type Props = {
  children: ReactNode;
};

export const FeatureFlagsProvider = ({ children }: Props) => {
  const client = useLDClient();
  const flags = useFlags();

  useLayoutEffect(() => {
    // @ts-expect-error Unknown global property on window
    // eslint-disable-next-line no-underscore-dangle
    window.__CLOUDFRONT_ASSETS = flags.useCloudfrontAssets;
  }, [flags.useCloudfrontAssets]);

  const value = useMemo(() => {
    const forcedFlag = sessionStorage.getItem('forcedFlag') || {};
    // avoid spreading all flags as this logs them as being still all used
    const proxiedFlags = new Proxy<typeof flags>(flags, {
      get(allFlags, prop, receiver) {
        const actual = Reflect.get(allFlags, prop, receiver);
        if (
          (Object.keys(forcedFlag) as (keyof typeof forcedFlag)[]).includes(
            prop
          )
        ) {
          return forcedFlag[prop];
        }
        return actual;
      },
    });

    const allFlags = client?.allFlags() ?? {};

    const trackedFlags = new Set(allFlags['monitored-feature-flags'] ?? []);

    const monitoredFlags = Object.fromEntries(
      Object.entries(allFlags).filter(([key]) => trackedFlags.has(key))
    );

    const identify: FeatureFlagsContextType['identify'] = !client
      ? undefined
      : async (
          { custom = {}, privateAttributeNames, ...user },
          hash?,
          onDone?
        ) =>
          client.identify(
            {
              kind: 'user',
              ...user,
              ...custom,
              _meta: {
                privateAttributes: privateAttributeNames,
              },
            },
            hash,
            onDone
          );

    return {
      flags: proxiedFlags,
      monitoredFlags,
      identify,
    };
  }, [client, flags]);

  return <FeatureFlagsContext value={value}>{children}</FeatureFlagsContext>;
};
